import { toFloatInt } from 'helpers/NumberFormats';
import type { ProductList } from 'types/checkout';

type PlaceOrderSourceItem = Pick<ProductList, 'asin' | 'quantity' | 'summedItemTotal'> & Partial<Pick<ProductList, 'productId' | 'styleId'>>;
type PlaceOrderGroup = Pick<ProductList, 'asin'> & {
  productId: number | undefined;
  styleId: number | undefined;
  itemCount: number;
  price: number;
};

const toProductId = (value?: string): number | undefined => {
  const id = value === undefined ? undefined : toFloatInt(value);
  return typeof id === 'number' && Number.isInteger(id) && id >= 0 && id <= 0xffffffff ? id : undefined;
};

/**
 * Groups purchase lines by product and style for Amethyst PlaceOrder.
 * Keeps lines separate when either catalog identifier is missing or invalid.
 * summedItemTotal already includes the quantity and item discounts; do not multiply it again.
 */
export const serializePlaceOrderItems = (productList: PlaceOrderSourceItem[] = []) => {
  const groups = new Map<string, PlaceOrderGroup>();

  productList.forEach(({ asin, productId, styleId, quantity, summedItemTotal }, index) => {
    const numericProductId = toProductId(productId);
    const numericStyleId = toProductId(styleId);
    const key = numericProductId !== undefined && numericStyleId !== undefined ? `product:${numericProductId}-${numericStyleId}` : `line:${index}`;
    const group = groups.get(key);

    if (group) {
      group.itemCount += quantity;
      group.price += summedItemTotal;
    } else {
      groups.set(key, { asin, productId: numericProductId, styleId: numericStyleId, itemCount: quantity, price: summedItemTotal });
    }
  });

  return [...groups.values()].map(({ asin, productId, styleId, itemCount, price }) => ({
    asin,
    productId,
    styleId,
    supplementalData: {
      itemCount,
      price: Math.round(price * 100) / 100
    }
  }));
};
